import React from "react";
import { Input, Card, Row, Col } from "antd";
import {
	MedicineBoxOutlined,
	IdcardOutlined,
	SafetyCertificateOutlined,
	UserOutlined,
	BookOutlined,
} from "@ant-design/icons";
import { TeamMember } from "../../../types/admin/TeamTypes";
// Direct path, deliberately NOT via the shared barrel — see the note in
// components/shared/index.ts (this is the only "shared" component that fetches over the
// network, and the barrel is imported by many unrelated tests that don't expect that).
import PractitionerColorPicker from "../../shared/PractitionerColorPicker";
import type { EditUserModalMode } from "../EditUserModal";

const { TextArea } = Input;

interface TeamMemberInfoTabProps {
	updatedTeamMember: TeamMember;
	setUpdatedTeamMember: React.Dispatch<React.SetStateAction<any>>;
	/**
	 * S5 (86d420t5u) — the calendar-colour section must be hidden entirely in "admin" mode:
	 * that mode saves via updateUser -> PUT /admin/users/{id}, which does not handle
	 * team_member_info at all, so a colour picked there would be silently discarded. This tab
	 * is currently only mounted in "team" mode (EditUserModal's adminTabs omits it), but the
	 * prop is a defence-in-depth guard at the component's own boundary rather than relying
	 * solely on that outer wiring.
	 */
	mode?: EditUserModalMode;
}

const TeamMemberInfoTab: React.FC<TeamMemberInfoTabProps> = ({
	updatedTeamMember,
	setUpdatedTeamMember,
	mode = "team",
}) => {
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setUpdatedTeamMember((prev: TeamMember) => ({
			...prev,
			team_member_info: { ...prev.team_member_info, [name]: value },
		}));
	};

	const handleColorChange = (hex: string | null) => {
		setUpdatedTeamMember((prev: TeamMember) => ({
			...prev,
			team_member_info: { ...prev.team_member_info, color: hex },
		}));
	};

	const handleArrayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const arrayValues = value.split(",").map((item) => item.trim());
		setUpdatedTeamMember((prev: TeamMember) => ({
			...prev,
			team_member_info: { ...prev.team_member_info, [name]: arrayValues },
		}));
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* Calendar Colour — hidden in "admin" mode; see the prop docblock above */}
			{mode !== "admin" && (
				<Card title="Calendar Colour" size="small">
					<PractitionerColorPicker
						value={updatedTeamMember.team_member_info?.color ?? null}
						onChange={handleColorChange}
						currentUserId={updatedTeamMember.id}
					/>
				</Card>
			)}

			{/* Professional Credentials */}
			<Card title="Professional Credentials" size="small">
				<Row gutter={[16, 16]}>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Medical License Number</label>
						<Input
							name="medical_license_number"
							value={updatedTeamMember.team_member_info?.medical_license_number || ""}
							onChange={handleInputChange}
							placeholder="Enter medical license number"
							prefix={<MedicineBoxOutlined />}
						/>
					</Col>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>NPI Number</label>
						<Input
							name="npi_number"
							value={updatedTeamMember.team_member_info?.npi_number || ""}
							onChange={handleInputChange}
							placeholder="Enter NPI number"
							prefix={<IdcardOutlined />}
						/>
					</Col>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>DEA Number</label>
						<Input
							name="dea_number"
							value={updatedTeamMember.team_member_info?.dea_number || ""}
							onChange={handleInputChange}
							placeholder="Enter DEA number"
							prefix={<SafetyCertificateOutlined />}
						/>
					</Col>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Taxonomy Code</label>
						<Input
							name="taxonomy_code"
							value={updatedTeamMember.team_member_info?.taxonomy_code || ""}
							onChange={handleInputChange}
							placeholder="Enter taxonomy code"
							prefix={<IdcardOutlined />}
						/>
					</Col>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Supervisor ID</label>
						<Input
							name="supervisor_id"
							value={String(updatedTeamMember.team_member_info?.supervisor_id ?? "")}
							onChange={handleInputChange}
							placeholder="Enter supervisor ID"
							prefix={<UserOutlined />}
						/>
					</Col>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Years of Experience</label>
						<Input
							name="years_of_experience"
							type="number"
							value={String(updatedTeamMember.team_member_info?.years_of_experience ?? 0)}
							onChange={handleInputChange}
							placeholder="Enter years of experience"
							min={0}
						/>
					</Col>
				</Row>
			</Card>

			{/* Specializations & Qualifications */}
			<Card title="Specializations & Qualifications" size="small">
				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Specialties</label>
						<Input
							name="specialties"
							value={updatedTeamMember.team_member_info?.specialties?.join(", ") || ""}
							onChange={handleArrayChange}
							placeholder="Enter specialties (comma separated)"
							prefix={<MedicineBoxOutlined />}
						/>
						<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>Separate with commas</span>
					</Col>
					<Col xs={24} md={12}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Qualifications</label>
						<Input
							name="qualifications"
							value={updatedTeamMember.team_member_info?.qualifications?.join(", ") || ""}
							onChange={handleArrayChange}
							placeholder="Enter qualifications (comma separated)"
							prefix={<BookOutlined />}
						/>
						<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>Separate with commas</span>
					</Col>
					<Col xs={24} md={12}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Certifications</label>
						<Input
							name="certifications"
							value={updatedTeamMember.team_member_info?.certifications?.join(", ") || ""}
							onChange={handleArrayChange}
							placeholder="Enter certifications (comma separated)"
							prefix={<SafetyCertificateOutlined />}
						/>
						<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>Separate with commas</span>
					</Col>
					<Col xs={24} md={12}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Professional Memberships</label>
						<Input
							name="professional_memberships"
							value={updatedTeamMember.team_member_info?.professional_memberships?.join(", ") || ""}
							onChange={handleArrayChange}
							placeholder="Enter memberships (comma separated)"
							prefix={<UserOutlined />}
						/>
						<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>Separate with commas</span>
					</Col>
				</Row>
			</Card>

			{/* Additional Information */}
			<Card title="Additional Information" size="small">
				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Languages Spoken</label>
						<Input
							name="languages_spoken"
							value={updatedTeamMember.team_member_info?.languages_spoken?.join(", ") || ""}
							onChange={handleArrayChange}
							placeholder="Enter languages (comma separated)"
						/>
						<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>Separate with commas</span>
					</Col>
					<Col xs={24} md={12}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Linkage</label>
						<Input
							name="linkage"
							value={(updatedTeamMember.team_member_info as any)?.linkage || ""}
							onChange={handleInputChange}
							placeholder="Enter linkage information"
						/>
					</Col>
					<Col xs={24}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Biography</label>
						<TextArea
							name="biography"
							value={updatedTeamMember.team_member_info?.biography || ""}
							onChange={handleInputChange}
							rows={4}
							placeholder="Enter professional biography"
							showCount
							maxLength={1000}
						/>
					</Col>
				</Row>
			</Card>
		</div>
	);
};

export default TeamMemberInfoTab;
